import { create } from 'zustand';
import * as THREE from 'three';
import { generateLayout } from '@arkturian/wave-engine/tessellation/layouts';
import { planSphereBands, computeSphereBandMorph } from '@arkturian/wave-engine/tessellation/tessellation';
import type { MorphConfig, LayoutShape, LayoutMode, LayoutParams, AtlasEntry } from '@arkturian/wave-engine/core/types';
import atlasRegistry from '@arkturian/wave-engine/atlas/AtlasRegistry';

// --- Tessellated morph calculation (mirrors tessellation.ts math for real-time slider updates) ---

function computeRingMorph(radius: number, depth: number, segments: number, tileAspect: number, gf: number): MorphConfig {
    const theta = (2 * Math.PI) / segments;
    const rOuter = radius + depth / 2;
    const rInner = Math.max(0.001, radius - depth / 2);
    const sizeX = 2 * rOuter * Math.sin(theta / 2) * gf;
    return {
        sizeX,
        sizeY: sizeX * tileAspect,
        depth,
        trapezoidX: 0,
        trapezoidY: 0,
        trapezoidZ: -(1 - rInner / rOuter),
    };
}

function computeCylinderMorph(radius: number, totalParticles: number, cylinderHeight: number, gf: number): MorphConfig {
    const depth = radius;
    const circumference = 2 * Math.PI * radius;
    const aspect = circumference / cylinderHeight;
    const segments = Math.max(3, Math.round(Math.sqrt(totalParticles * aspect)));
    const rows = Math.max(1, Math.ceil(totalParticles / segments));
    const rowHeight = cylinderHeight / rows;
    const theta = (2 * Math.PI) / segments;
    return {
        sizeX: 2 * radius * Math.sin(theta / 2) * gf,
        sizeY: rowHeight * gf,
        depth,
        trapezoidX: 0,
        trapezoidY: 0,
        trapezoidZ: -0.99,
    };
}

// Atlas Registry is now in engine/atlas/AtlasRegistry.ts

// --- State and Actions Interfaces ---

export interface GPUParticleStoreState {
    isInitialized: boolean;
    canvas: HTMLCanvasElement | null;

    particleCount: number;
    layoutShape: LayoutShape;
    layoutMode: LayoutMode;
    shapeSize: number;
    particleSize: number;

    targetSize: number;
    targetSizeY: number;
    targetDepth: number;
    targetTrapezoidFactorX: number;
    targetTrapezoidFactorY: number;
    targetTrapezoidFactorZ: number;

    color1: string;
    color2: string;
    colorMix: number;
    roughness: number;
    metalness: number;
    emissive: string;
    emissiveIntensity: number;
    bgColor: string;

    // --- Atlas ---
    activeAtlasId: string;  // key into atlasRegistry ('' = none)
    atlasRows: number;
    atlasCols: number;
    atlasUrl: string;       // LOD 0 URL (for texture loading)
    atlasTileCount: number;
    atlasFaceMode: number;
    atlasLodCount: number;  // how many LOD levels available
    galleryRedux: number;  // 0-1, how much of sphere gallery covers
    tileAspect: number;    // tile height relative to width (1=square, 0.5=landscape, 2=portrait)
    boxRatio: number;      // box depth/width ratio (1=square, 0.5=wide, 2=deep)
    gapFactor: number;     // 0-1, tile gap (1=seamless, 0.9=10% gap)

    // --- Lighting ---
    ambientIntensity: number;
    keyLightIntensity: number;
    headLightIntensity: number;
    lodThreshold: number;
    lod2Threshold: number;

    // --- Render Quality ---
    renderScale: number;
    cornerRadius: number;   // 0 = sharp box, 0.1-0.5 = rounded corners+edges
    dataMode: boolean;      // false=normal, true=aTarget.w is data value
    dataReveal: number;     // 0-1, animation from flat to data-driven depth
    depthDirection: number; // 0=symmetric, 1=back only, 2=front only
    showDebugBBox: boolean; // show wireframe bounding boxes for hit test debug

    // --- Field Animation ---
    fieldAnimStrength: number;
    waveSpeed: number;
    waveAmplitude: number;
    breathingSpeed: number;
    breathingStrength: number;
    waveDirX: number;
    waveDirY: number;
    waveDirZ: number;
    lineThickness: number;
    overrideFitMode: number; // 0=UV crop, 1=shape fit

    // --- Ripple Effect ---
    rippleStrength: number;
    rippleFrequency: number;
    rippleSpeed: number;

    // --- Per-tile aspect ---
    tileAspectMode: 'square' | 'individual' | 'fixed';
    tileFixedAspect: number; // for 'fixed' mode: w/h ratio (1.778=16:9, 0.5625=9:16, etc.)
    tileAspectData: Float32Array | null;

    // --- Layout orientation settings (for library layouts) ---
    layoutOrientation: 'aligned' | 'perpendicular';
    layoutUpMode: 'world' | 'path';
    layoutSpacingMode: 'margin' | 'distribute';
    layoutSpacing: number;
    spiralTurns: number;
    spiralGrowthRate: number;

    // --- Filter ---
    filterEnabled: boolean;
    filterBucketLabel: string | null;
    filterMatchIndices: number[] | null;
    filterLineFace: 'front' | 'back' | 'center';
}

export interface GPUParticleStoreActions {
    initialize: (canvas: HTMLCanvasElement) => void;
    setCanvas: (canvas: HTMLCanvasElement) => void;
    initializeParticles: (mesh: THREE.InstancedMesh) => void;
    unmount: () => void;

    setParticleCount: (count: number) => void;
    setLayoutShape: (shape: LayoutShape) => void;
    setLayoutMode: (mode: LayoutMode) => void;
    setShapeSize: (size: number) => void;
    setParticleSize: (size: number) => void;

    setTargetSize: (mesh: THREE.InstancedMesh, size: number) => void;
    setTargetSizeY: (mesh: THREE.InstancedMesh, size: number) => void;
    setTargetDepth: (mesh: THREE.InstancedMesh, depth: number) => void;
    setTargetTrapezoidFactorX: (mesh: THREE.InstancedMesh, factor: number) => void;
    setTargetTrapezoidFactorY: (mesh: THREE.InstancedMesh, factor: number) => void;
    setTargetTrapezoidFactorZ: (mesh: THREE.InstancedMesh, factor: number) => void;

    setColor1: (color: string) => void;
    setColor2: (color: string) => void;
    setColorMix: (mix: number) => void;
    setRoughness: (roughness: number) => void;
    setMetalness: (metalness: number) => void;
    setEmissive: (color: string) => void;
    setEmissiveIntensity: (intensity: number) => void;
    setBgColor: (color: string) => void;
    setAmbientIntensity: (v: number) => void;
    setKeyLightIntensity: (v: number) => void;
    setHeadLightIntensity: (v: number) => void;
    setLodThreshold: (v: number) => void;
    setLod2Threshold: (v: number) => void;

    setRenderScale: (v: number) => void;
    setCornerRadius: (v: number) => void;
    setDataMode: (v: boolean) => void;
    setDataReveal: (v: number) => void;
    setDepthDirection: (v: number) => void;
    setShowDebugBBox: (v: boolean) => void;
    setPerParticleData: (mesh: THREE.InstancedMesh, values: number[]) => void;
    setFieldAnimStrength: (v: number) => void;
    setWaveSpeed: (v: number) => void;
    setWaveAmplitude: (v: number) => void;
    setBreathingSpeed: (v: number) => void;
    setBreathingStrength: (v: number) => void;
    setWaveDirX: (v: number) => void;
    setWaveDirY: (v: number) => void;
    setWaveDirZ: (v: number) => void;
    setLineThickness: (v: number) => void;
    setOverrideFitMode: (v: number) => void;
    setRippleStrength: (v: number) => void;
    setRippleFrequency: (v: number) => void;
    setRippleSpeed: (v: number) => void;

    // --- Filter ---
    setFilterBucket: (label: string | null, indices: number[] | null) => void;
    setFilterLineFace: (face: 'front' | 'back' | 'center') => void;

    // --- Per-tile aspect ---
    setTileAspectMode: (mode: 'square' | 'individual' | 'fixed') => void;
    setTileFixedAspect: (aspect: number) => void;
    setTileAspectData: (data: Float32Array | null) => void;
    loadTileAspectData: (manifestUrl: string, mesh?: THREE.InstancedMesh) => Promise<void>;
    applyTileAspectSizes: (mesh: THREE.InstancedMesh) => void;

    // --- Layout orientation (library layouts) ---
    setLayoutOrientation: (v: 'aligned' | 'perpendicular') => void;
    setLayoutUpMode: (v: 'world' | 'path') => void;
    setLayoutSpacingMode: (v: 'margin' | 'distribute') => void;
    setLayoutSpacing: (v: number) => void;
    setSpiralTurns: (v: number) => void;
    setSpiralGrowthRate: (v: number) => void;

    // --- Atlas ---
    loadAtlasById: (mesh: THREE.InstancedMesh, id: string) => void;
    clearAtlas: (mesh: THREE.InstancedMesh) => void;
    getAtlasRegistry: () => any;
    getActiveAtlas: () => AtlasEntry | null;
    setAtlasFaceMode: (mode: number) => void;
    setGalleryRedux: (value: number) => void;
    setTileAspect: (value: number) => void;
    setBoxRatio: (value: number) => void;
    setGapFactor: (value: number) => void;
}

// --- Store Implementation ---

const useGPUParticleStore = create<GPUParticleStoreState & GPUParticleStoreActions>()(
    (set, get) => {

        // Central morph applicator: for tessellated layouts (ring), recalculates
        // all dependent morph params. For others, writes global values directly.
        // Write morph to aTarget2 .xy while preserving .zw (old values for transitions)
        function setTarget2XY(attr: THREE.InstancedBufferAttribute, i: number, trapZ: number, sizeY: number) {
            const oldZ = attr.getZ(i);
            const oldW = attr.getW(i);
            attr.setXYZW(i, trapZ, sizeY, oldZ, oldW);
        }

        function applyMorph(mesh: THREE.InstancedMesh) {
            const state = get();
            const targetAttr = mesh.geometry.getAttribute('aTarget') as THREE.InstancedBufferAttribute;
            const target2Attr = mesh.geometry.getAttribute('aTarget2') as THREE.InstancedBufferAttribute;
            const count = state.particleCount;

            const gf = state.gapFactor;

            if (state.layoutShape === 'ring') {
                const m = computeRingMorph(state.shapeSize, state.targetDepth, count, state.tileAspect, gf);
                for (let i = 0; i < count; i++) {
                    targetAttr.setXYZW(i, m.sizeX, m.depth, m.trapezoidX, m.trapezoidY);
                    setTarget2XY(target2Attr, i, m.trapezoidZ, m.sizeY);
                }
            } else if (state.layoutShape === 'tube') {
                // Tube = hollow cylinder, same math as ring but with row/segment split
                const cylinderHeight = state.shapeSize * 2;
                const circumference = 2 * Math.PI * state.shapeSize;
                const tubeAspect = circumference / cylinderHeight;
                const segments = Math.max(3, Math.round(Math.sqrt(count * tubeAspect)));
                const rows = Math.max(1, Math.ceil(count / segments));
                // Compute effective tileAspect from row height / sizeX
                const sizeX = 2 * (state.shapeSize + state.targetDepth / 2) * Math.sin(Math.PI / segments) * gf;
                const rowHeight = (cylinderHeight / rows) * gf;
                const effectiveAspect = sizeX > 0 ? rowHeight / sizeX : 1;
                const m = computeRingMorph(state.shapeSize, state.targetDepth, segments, effectiveAspect, gf);
                for (let i = 0; i < count; i++) {
                    targetAttr.setXYZW(i, m.sizeX, m.depth, m.trapezoidX, m.trapezoidY);
                    setTarget2XY(target2Attr, i, m.trapezoidZ, m.sizeY);
                }
            } else if (state.layoutShape === 'cylinder') {
                const cylinderHeight = state.shapeSize * 2;
                const m = computeCylinderMorph(state.shapeSize, count, cylinderHeight, gf);
                for (let i = 0; i < count; i++) {
                    targetAttr.setXYZW(i, m.sizeX, m.depth, m.trapezoidX, m.trapezoidY);
                    setTarget2XY(target2Attr, i, m.trapezoidZ, m.sizeY);
                }
            } else if (state.layoutShape === 'sphere') {
                const plan = planSphereBands(count);
                let idx = 0;
                for (const band of plan) {
                    const m = computeSphereBandMorph(band, state.shapeSize, state.targetDepth, gf);
                    for (let s = 0; s < band.segments && idx < count; s++, idx++) {
                        targetAttr.setXYZW(idx, m.sizeX, m.depth, m.trapezoidX, m.trapezoidY);
                        setTarget2XY(target2Attr, idx, m.trapezoidZ, m.sizeY);
                    }
                }
            } else {
                for (let i = 0; i < count; i++) {
                    targetAttr.setXYZW(i, state.targetSize, state.targetDepth, state.targetTrapezoidFactorX, state.targetTrapezoidFactorY);
                    setTarget2XY(target2Attr, i, state.targetTrapezoidFactorZ, state.targetSizeY);
                }
            }

            targetAttr.needsUpdate = true;
            target2Attr.needsUpdate = true;
        }

        return {
            isInitialized: false,
            canvas: null,

            particleCount: 4096,
            layoutShape: 'box' as LayoutShape,
            layoutMode: 'fixedShapeSize' as LayoutMode,
            shapeSize: 3,
            particleSize: 0.1,

            targetSize: 1.0,
            targetSizeY: 1.0,
            targetDepth: 0.02,
            targetTrapezoidFactorX: 0.0,
            targetTrapezoidFactorY: 0.0,
            targetTrapezoidFactorZ: 0.0,

            color1: '#ff0000',
            color2: '#0000ff',
            colorMix: 0.0,
            roughness: 0.5,
            metalness: 0.5,
            emissive: '#000000',
            emissiveIntensity: 1,
            bgColor: '#ffffff',

            ambientIntensity: 1.75,
            keyLightIntensity: 2.9,
            headLightIntensity: 3.0,
            lodThreshold: 5.0,
            lod2Threshold: 2.0,

            renderScale: 2,
            cornerRadius: 0,
            dataMode: false,
            dataReveal: 1,
            depthDirection: 2,
            showDebugBBox: false,

            fieldAnimStrength: 0,
            waveSpeed: 1.5,
            waveAmplitude: 0.15,
            breathingSpeed: 0.8,
            breathingStrength: 0.03,
            waveDirX: 0,
            waveDirY: 1,
            waveDirZ: 0,
            lineThickness: 1.0,
            overrideFitMode: 1,
            rippleStrength: 0.025,
            rippleFrequency: 12.0,
            rippleSpeed: 3.0,

            tileAspectMode: 'individual' as const,
            tileFixedAspect: 1.0,
            tileAspectData: null as Float32Array | null,

            layoutOrientation: 'aligned' as const,
            layoutUpMode: 'world' as const,
            layoutSpacingMode: 'distribute' as const,
            layoutSpacing: 1.5,
            spiralTurns: 2.0,
            spiralGrowthRate: 2.0,

            filterEnabled: false,
            filterBucketLabel: null,
            filterMatchIndices: null,
            filterLineFace: 'front' as const,

            activeAtlasId: 'photos_4096_pad',
            atlasRows: 64,
            atlasCols: 64,
            atlasUrl: '/atlas_pad__64-64-3-0-1.jpg',
            atlasTileCount: 4096,
            atlasFaceMode: 1,
            atlasLodCount: 3,
            galleryRedux: 0.21,
            tileAspect: 1.0,
            boxRatio: 1.0,
            gapFactor: 0.95,

            initialize: (canvas) => {
                set({ isInitialized: true, canvas });
            },

            setCanvas: (canvas) => {
                set({ canvas });
            },

            initializeParticles: (mesh) => {
                const state = get();
                if (!mesh) return;

                const layoutParams: LayoutParams = {
                    particleCount: state.particleCount,
                    shape: state.layoutShape,
                    mode: state.layoutMode,
                    targetRadius: state.shapeSize,
                    baseParticleSize: new THREE.Vector2(state.particleSize, state.particleSize),
                    targetDepth: state.targetDepth,
                    targetSizeY: state.targetSizeY,
                    galleryRedux: state.galleryRedux,
                    tileAspect: state.tileAspect,
                    boxRatio: state.boxRatio,
                    gapFactor: state.gapFactor,
                };

                const layoutData = generateLayout(layoutParams);

                // Sync store morph values with the layout's calculated morph.
                // This makes sliders reflect the actual particle state after a layout change,
                // so the user can tweak from the layout's calculated baseline.
                if (layoutData.length > 0 && layoutData[0].target.morph) {
                    const m = layoutData[0].target.morph;
                    set({
                        targetSize: m.sizeX,
                        targetSizeY: m.sizeY,
                        targetDepth: m.depth,
                        targetTrapezoidFactorX: m.trapezoidX,
                        targetTrapezoidFactorY: m.trapezoidY,
                        targetTrapezoidFactorZ: m.trapezoidZ,
                    });
                }


                const positionAttribute = mesh.geometry.getAttribute('aLayout') as THREE.InstancedBufferAttribute;
                const quaternionAttribute = mesh.geometry.getAttribute('aQuaternion') as THREE.InstancedBufferAttribute;
                const targetAttribute = mesh.geometry.getAttribute('aTarget') as THREE.InstancedBufferAttribute;
                const targetAttribute2 = mesh.geometry.getAttribute('aTarget2') as THREE.InstancedBufferAttribute;

                const bufferCount = positionAttribute.count;

                for (let i = 0; i < bufferCount; i++) {
                    // Snapshot current .xy → .zw (old values for transition)
                    const oldTrapZ = targetAttribute2.getX(i);
                    const oldSizeY = targetAttribute2.getY(i);

                    const data = i < layoutData.length ? layoutData[i] : null;
                    if (data) {
                        positionAttribute.setXYZW(i, data.target.position.x, data.target.position.y, data.target.position.z, data.target.scale.x);
                        quaternionAttribute.setXYZW(i, data.target.quaternion.x, data.target.quaternion.y, data.target.quaternion.z, data.target.quaternion.w);

                        // Preserve .w (data value) if data mode is active — Layer 3 owns aTarget.w
                        const existingW = targetAttribute.getW(i);

                        if (data.target.morph) {
                            const m = data.target.morph;
                            targetAttribute.setXYZW(i, m.sizeX, m.depth, m.trapezoidX, state.dataMode ? existingW : m.trapezoidY);
                            targetAttribute2.setXYZW(i, m.trapezoidZ, m.sizeY, oldTrapZ, oldSizeY);
                        } else {
                            targetAttribute.setXYZW(i, state.targetSize, state.targetDepth, state.targetTrapezoidFactorX, state.dataMode ? existingW : state.targetTrapezoidFactorY);
                            targetAttribute2.setXYZW(i, state.targetTrapezoidFactorZ, state.targetSizeY, oldTrapZ, oldSizeY);
                        }
                    } else {
                        positionAttribute.setXYZW(i, 0, 0, 0, 0);
                        quaternionAttribute.setXYZW(i, 0, 0, 0, 1);
                        targetAttribute.setXYZW(i, 1, 1, 0, 0);
                        targetAttribute2.setXYZW(i, 0, 1, oldTrapZ, oldSizeY);
                    }
                }

                // Assign atlas UV offsets per particle
                const uvAttr = mesh.geometry.getAttribute('aUVOffset') as THREE.InstancedBufferAttribute;
                const { atlasRows, atlasCols, atlasTileCount } = get();
                if (atlasRows > 0 && atlasCols > 0 && atlasTileCount > 0) {
                    for (let i = 0; i < bufferCount; i++) {
                        const tileIndex = i % atlasTileCount;
                        const col = tileIndex % atlasCols;
                        const row = Math.floor(tileIndex / atlasCols);
                        const scaleU = 1 / atlasCols;
                        const scaleV = 1 / atlasRows;
                        const offsetU = col * scaleU;
                        const offsetV = 1 - (row + 1) * scaleV; // Y flipped
                        uvAttr.setXYZW(i, offsetU, offsetV, scaleU, scaleV);
                    }
                } else {
                    // No atlas: each particle gets full UV (0,0,1,1)
                    for (let i = 0; i < bufferCount; i++) {
                        uvAttr.setXYZW(i, 0, 0, 1, 1);
                    }
                }

                // Apply per-tile aspect ratios when in individual mode
                // Skip for aspect-aware layouts (line_h, line_v, masonry_v, masonry_h) — they compute sizes internally
                const aspectLayouts = ['line_h', 'line_v', 'ring_h', 'ring_v', 'masonry_v', 'masonry_h', 'spiral_a', 'sphere_grid_a', 'sphere_hex_a', 'plane_grid_a', 'plane_hex_a', 'cylinder_grid_a'];
                const { tileAspectMode, tileAspectData, tileFixedAspect } = get();
                if ((tileAspectMode === 'individual' || tileAspectMode === 'fixed') && !aspectLayouts.includes(state.layoutShape)) {
                    for (let i = 0; i < bufferCount; i++) {
                        const baseSize = targetAttribute.getX(i);
                        let aspect: number;
                        if (tileAspectMode === 'fixed') {
                            aspect = tileFixedAspect || 1.0;
                        } else {
                            aspect = tileAspectData && i < tileAspectData.length ? tileAspectData[i] : 1.0;
                        }
                        // Contain: shrink within the layout cell, never grow beyond it.
                        // Grid-based layouts position tiles assuming tile size == cell size —
                        // growing a dimension overlaps neighbor cells.
                        if (aspect >= 1.0) {
                            targetAttribute2.setY(i, targetAttribute2.getY(i) / aspect);
                        } else {
                            targetAttribute.setX(i, baseSize * aspect);
                        }
                    }
                }

                positionAttribute.needsUpdate = true;
                quaternionAttribute.needsUpdate = true;
                targetAttribute.needsUpdate = true;
                targetAttribute2.needsUpdate = true;
                uvAttr.needsUpdate = true;
            },

            unmount: () => {
                set({ isInitialized: false, canvas: null });
            },

            // --- UI Setters ---
            setParticleCount: (count) => set({ particleCount: count }),
            setLayoutShape: (shape) => set({ layoutShape: shape }),
            setLayoutMode: (mode) => set({ layoutMode: mode }),
            setShapeSize: (size) => set({ shapeSize: size }),
            setParticleSize: (size) => set({ particleSize: size }),

            // --- Morph Setters: update state, then applyMorph recalculates everything ---
            setTargetSize: (mesh, size) => {
                set({ targetSize: size });
                if (mesh) applyMorph(mesh);
            },
            setTargetSizeY: (mesh, size) => {
                set({ targetSizeY: size });
                if (mesh) applyMorph(mesh);
            },
            setTargetDepth: (mesh, depth) => {
                set({ targetDepth: depth });
                if (mesh) applyMorph(mesh);
            },
            setTargetTrapezoidFactorX: (mesh, factor) => {
                set({ targetTrapezoidFactorX: factor });
                if (mesh) applyMorph(mesh);
            },
            setTargetTrapezoidFactorY: (mesh, factor) => {
                set({ targetTrapezoidFactorY: factor });
                if (mesh) applyMorph(mesh);
            },
            setTargetTrapezoidFactorZ: (mesh, factor) => {
                set({ targetTrapezoidFactorZ: factor });
                if (mesh) applyMorph(mesh);
            },

            // --- Material & Color Setters ---
            setColor1: (color) => set({ color1: color }),
            setColor2: (color) => set({ color2: color }),
            setColorMix: (mix) => set({ colorMix: mix }),
            setRoughness: (roughness) => set({ roughness }),
            setMetalness: (metalness) => set({ metalness }),
            setEmissive: (color) => set({ emissive: color }),
            setEmissiveIntensity: (intensity) => set({ emissiveIntensity: intensity }),
            setBgColor: (color) => set({ bgColor: color }),
            setAmbientIntensity: (v) => set({ ambientIntensity: v }),
            setKeyLightIntensity: (v) => set({ keyLightIntensity: v }),
            setHeadLightIntensity: (v) => set({ headLightIntensity: v }),
            setLodThreshold: (v) => set({ lodThreshold: v }),
            setLod2Threshold: (v) => set({ lod2Threshold: v }),

            setRenderScale: (v) => set({ renderScale: v }),
            setCornerRadius: (v) => set({ cornerRadius: v }),
            setDataMode: (v) => set({ dataMode: v }),
            setDataReveal: (v) => set({ dataReveal: v }),
            setDepthDirection: (v) => set({ depthDirection: v }),
            setShowDebugBBox: (v) => set({ showDebugBBox: v }),

            // Write per-particle data values into aTarget.w (trapezoidY slot)
            setPerParticleData: (mesh, values) => {
                if (!mesh) return;
                const targetAttr = mesh.geometry.getAttribute('aTarget') as THREE.InstancedBufferAttribute;
                for (let i = 0; i < values.length && i < targetAttr.count; i++) {
                    targetAttr.setW(i, values[i]); // Write to .w (trapezoidY/dataValue)
                }
                targetAttr.needsUpdate = true;
            },
            setFieldAnimStrength: (v) => set({ fieldAnimStrength: v }),
            setWaveSpeed: (v) => set({ waveSpeed: v }),
            setWaveAmplitude: (v) => set({ waveAmplitude: v }),
            setBreathingSpeed: (v) => set({ breathingSpeed: v }),
            setBreathingStrength: (v) => set({ breathingStrength: v }),
            setWaveDirX: (v) => set({ waveDirX: v }),
            setWaveDirY: (v) => set({ waveDirY: v }),
            setWaveDirZ: (v) => set({ waveDirZ: v }),
            setLineThickness: (v) => set({ lineThickness: v }),
            setOverrideFitMode: (v) => set({ overrideFitMode: v }),
            setRippleStrength: (v) => set({ rippleStrength: v }),
            setRippleFrequency: (v) => set({ rippleFrequency: v }),
            setRippleSpeed: (v) => set({ rippleSpeed: v }),

            // --- Filter ---
            setFilterBucket: (label, indices) => set({
                filterEnabled: !!label,
                filterBucketLabel: label,
                filterMatchIndices: indices,
            }),
            setFilterLineFace: (face) => set({ filterLineFace: face }),

            // --- Layout orientation (library layouts) ---
            setLayoutOrientation: (v) => set({ layoutOrientation: v }),
            setLayoutUpMode: (v) => set({ layoutUpMode: v }),
            setLayoutSpacingMode: (v) => set({ layoutSpacingMode: v }),
            setLayoutSpacing: (v) => set({ layoutSpacing: v }),
            setSpiralTurns: (v) => set({ spiralTurns: v }),
            setSpiralGrowthRate: (v) => set({ spiralGrowthRate: v }),

            // --- Per-tile aspect ---
            setTileAspectMode: (mode) => set({ tileAspectMode: mode }),
            setTileFixedAspect: (aspect) => set({ tileFixedAspect: aspect }),
            setTileAspectData: (data) => set({ tileAspectData: data }),

            loadTileAspectData: async (manifestUrl: string, mesh?: THREE.InstancedMesh) => {
                try {
                    const resp = await fetch(manifestUrl);
                    const manifest = await resp.json();
                    // Support both manifest formats: v1 (products object) and v2 (atlases array)
                    let aspectData: Float32Array;
                    if (manifest.atlases?.[0]?.tiles) {
                        const tiles = manifest.atlases[0].tiles;
                        aspectData = new Float32Array(tiles.length);
                        for (const tile of tiles) {
                            if (tile.index >= 0 && tile.index < tiles.length) {
                                aspectData[tile.index] = tile.source_aspect ?? 1.0;
                            }
                        }
                    } else {
                        return;
                    }
                    set({ tileAspectData: aspectData });
                    // Sync to EngineRuntime so aspect layouts can access it
                    const { EngineRuntime } = await import('@arkturian/wave-engine/core/EngineRuntime');
                    EngineRuntime.getInstance().tileAspectData = aspectData;
                    // Auto-apply if mesh available and individual mode (skip for aspect-aware layouts)
                    const m = mesh || null;
                    const aspectLayouts = ['line_h', 'line_v', 'ring_h', 'ring_v', 'masonry_v', 'masonry_h', 'spiral_a', 'sphere_grid_a', 'sphere_hex_a', 'plane_grid_a', 'plane_hex_a', 'cylinder_grid_a'];
                    if (m && get().tileAspectMode === 'individual' && !aspectLayouts.includes(get().layoutShape)) {
                        setTimeout(() => get().applyTileAspectSizes(m), 200);
                    }
                } catch (e) {
                    console.warn('[Store] Failed to load tile aspect data:', e);
                }
            },

            applyTileAspectSizes: (mesh: THREE.InstancedMesh) => {
                const state = get();
                if (!mesh) return;
                const { tileAspectMode, tileAspectData, particleCount } = state;
                const targetAttr = mesh.geometry.getAttribute('aTarget') as THREE.InstancedBufferAttribute;
                const target2Attr = mesh.geometry.getAttribute('aTarget2') as THREE.InstancedBufferAttribute;

                if (tileAspectMode === 'square') return;

                for (let i = 0; i < particleCount; i++) {
                    const baseSize = targetAttr.getX(i);
                    let aspect: number;
                    if (tileAspectMode === 'fixed') {
                        aspect = state.tileFixedAspect || 1.0;
                    } else {
                        aspect = tileAspectData && i < tileAspectData.length ? tileAspectData[i] : 1.0;
                    }
                    // Contain: max dimension stays at cell size, the other shrinks —
                    // grid layouts cannot compensate tiles growing beyond their cell.
                    let sizeX: number;
                    let sizeY: number;
                    if (aspect >= 1.0) {
                        // Landscape: full cell width, reduced height
                        sizeX = baseSize;
                        sizeY = baseSize / aspect;
                    } else {
                        // Portrait: full cell height, reduced width
                        sizeX = baseSize * aspect;
                        sizeY = baseSize;
                    }
                    // Write sizeX to aTarget.x
                    targetAttr.setX(i, sizeX);
                    // Write sizeY to aTarget2.y
                    target2Attr.setY(i, sizeY);
                }
                targetAttr.needsUpdate = true;
                target2Attr.needsUpdate = true;
            },

            // --- Atlas Registry ---
            getAtlasRegistry: () => atlasRegistry,

            getActiveAtlas: () => {
                const id = get().activeAtlasId;
                return id ? atlasRegistry.get(id) ?? null : null;
            },

            loadAtlasById: (mesh, id) => {
                const entry = atlasRegistry.get(id);
                if (!entry || !mesh) return;

                const lod0 = entry.lods[0];
                set({
                    activeAtlasId: id,
                    atlasUrl: lod0.urls[0],
                    atlasRows: lod0.rows,
                    atlasCols: lod0.cols,
                    atlasTileCount: entry.tileCount,
                    atlasLodCount: entry.lods.length,
                    particleCount: entry.tileCount,
                });

                // Recompute UV offsets (based on LOD 0 grid)
                const uvAttr = mesh.geometry.getAttribute('aUVOffset') as THREE.InstancedBufferAttribute;
                const bufferCount = uvAttr.count;
                for (let i = 0; i < bufferCount; i++) {
                    const tileIndex = i % entry.tileCount;
                    const col = tileIndex % lod0.cols;
                    const row = Math.floor(tileIndex / lod0.cols);
                    const scaleU = 1 / lod0.cols;
                    const scaleV = 1 / lod0.rows;
                    uvAttr.setXYZW(i, col * scaleU, 1 - (row + 1) * scaleV, scaleU, scaleV);
                }
                uvAttr.needsUpdate = true;

                // PAD atlas: auto-load aspect data + switch to individual mode
                if (entry.padMode && entry.manifestUrl) {
                    set({ tileAspectMode: 'individual' });
                    get().loadTileAspectData(entry.manifestUrl, mesh);
                } else {
                    // Non-PAD atlas: square mode, clear aspect data
                    set({ tileAspectData: null, tileAspectMode: 'square' });
                }
            },

            clearAtlas: (mesh) => {
                set({ activeAtlasId: '', atlasUrl: '', atlasRows: 0, atlasCols: 0, atlasTileCount: 0, atlasLodCount: 0 });
                if (!mesh) return;
                const uvAttr = mesh.geometry.getAttribute('aUVOffset') as THREE.InstancedBufferAttribute;
                for (let i = 0; i < uvAttr.count; i++) uvAttr.setXYZW(i, 0, 0, 1, 1);
                uvAttr.needsUpdate = true;
            },

            setAtlasFaceMode: (mode) => set({ atlasFaceMode: mode }),
            setGalleryRedux: (value) => set({ galleryRedux: value }),
            setTileAspect: (value) => set({ tileAspect: value }),
            setBoxRatio: (value) => set({ boxRatio: value }),
            setGapFactor: (value) => set({ gapFactor: value }),
        };
    }
);

export default useGPUParticleStore;
